import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { selectUserUid } from 'src/app/core/store/user/user.selectors';
import { PackingListsService } from '../services/packing-lists.service';
import * as PackingListsActions from './packing-lists.actions';

@Injectable()
export class PackingListsEffects {
  constructor(
    private actions$: Actions,
    private packingListsService: PackingListsService,
    private store: Store,
    private router: Router
  ) {}

  loadPackingLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.loadPackingLists),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([_, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.loadPackingListsFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService.getUserPackingLists(userId).pipe(
          map((packingLists) =>
            PackingListsActions.loadPackingListsSuccess({ packingLists })
          ),
          catchError((error) =>
            of(
              PackingListsActions.loadPackingListsFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  loadPackingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.loadPackingList),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.loadPackingListFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService.getPackingList(userId, listId).pipe(
          map((packingList) => {
            if (!packingList) {
              return PackingListsActions.loadPackingListFailure({
                error: 'Packing list not found',
              });
            }

            return PackingListsActions.loadPackingListSuccess({ packingList });
          }),
          catchError((error) =>
            of(
              PackingListsActions.loadPackingListFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  createPackingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.createPackingList),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ name }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.createPackingListFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService.createPackingList(userId, name).pipe(
          map((packingList) =>
            PackingListsActions.createPackingListSuccess({ packingList })
          ),
          catchError((error) =>
            of(
              PackingListsActions.createPackingListFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  createPackingListSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PackingListsActions.createPackingListSuccess),
        tap(({ packingList }) => {
          void this.router.navigate(['/tabs/packing-lists', packingList.id]);
        })
      ),
    { dispatch: false }
  );

  renamePackingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.renamePackingList),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId, name }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.renamePackingListFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService.renamePackingList(userId, listId, name).pipe(
          map((packingList) =>
            PackingListsActions.renamePackingListSuccess({ packingList })
          ),
          catchError((error) =>
            of(
              PackingListsActions.renamePackingListFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  deletePackingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.deletePackingList),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.deletePackingListFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService.deletePackingList(userId, listId).pipe(
          map(() => PackingListsActions.deletePackingListSuccess({ listId })),
          catchError((error) =>
            of(
              PackingListsActions.deletePackingListFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  deletePackingListSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PackingListsActions.deletePackingListSuccess),
        tap(() => {
          void this.router.navigate(['/tabs/packing-lists']);
        })
      ),
    { dispatch: false }
  );

  addPackingListItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.addPackingListItem),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId, label }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.addPackingListItemFailure({
              listId,
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService
          .addPackingListItem(userId, listId, label)
          .pipe(
            map((packingList) =>
              PackingListsActions.addPackingListItemSuccess({ packingList })
            ),
            catchError((error) =>
              of(
                PackingListsActions.addPackingListItemFailure({
                  listId,
                  error: error.message,
                })
              )
            )
          );
      })
    )
  );

  togglePackingListItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.togglePackingListItem),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId, itemId }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.togglePackingListItemFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService
          .togglePackingListItem(userId, listId, itemId)
          .pipe(
            map((packingList) =>
              PackingListsActions.togglePackingListItemSuccess({ packingList })
            ),
            catchError((error) =>
              of(
                PackingListsActions.togglePackingListItemFailure({
                  error: error.message,
                })
              )
            )
          );
      })
    )
  );

  renamePackingListItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.renamePackingListItem),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId, itemId, label }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.renamePackingListItemFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService
          .renamePackingListItem(userId, listId, itemId, label)
          .pipe(
            map((packingList) =>
              PackingListsActions.renamePackingListItemSuccess({ packingList })
            ),
            catchError((error) =>
              of(
                PackingListsActions.renamePackingListItemFailure({
                  error: error.message,
                })
              )
            )
          );
      })
    )
  );

  deletePackingListItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.deletePackingListItem),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId, itemId }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.deletePackingListItemFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService
          .deletePackingListItem(userId, listId, itemId)
          .pipe(
            map((packingList) =>
              PackingListsActions.deletePackingListItemSuccess({ packingList })
            ),
            catchError((error) =>
              of(
                PackingListsActions.deletePackingListItemFailure({
                  error: error.message,
                })
              )
            )
          );
      })
    )
  );

  bulkDeletePackingListItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.bulkDeletePackingListItems),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId, itemIds }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.bulkDeletePackingListItemsFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService
          .bulkDeletePackingListItems(userId, listId, itemIds)
          .pipe(
            map((packingList) =>
              PackingListsActions.bulkDeletePackingListItemsSuccess({
                packingList,
              })
            ),
            catchError((error) =>
              of(
                PackingListsActions.bulkDeletePackingListItemsFailure({
                  error: error.message,
                })
              )
            )
          );
      })
    )
  );

  reorderPackingListItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PackingListsActions.reorderPackingListItems),
      withLatestFrom(this.store.select(selectUserUid)),
      mergeMap(([{ listId, itemIds }, userId]) => {
        if (!userId) {
          return of(
            PackingListsActions.reorderPackingListItemsFailure({
              error: 'User not authenticated',
            })
          );
        }

        return this.packingListsService
          .reorderPackingListItems(userId, listId, itemIds)
          .pipe(
            map((packingList) =>
              PackingListsActions.reorderPackingListItemsSuccess({ packingList })
            ),
            catchError((error) =>
              of(
                PackingListsActions.reorderPackingListItemsFailure({
                  error: error.message,
                })
              )
            )
          );
      })
    )
  );
}
